<template>
  <div id="competition">
    <transition-group
      name="fade"
      appear
      mode="out-in"
      enter-active-class="animated fade slower"
      leave-active-class="animated fade slower"
      tag="div"
    >
      <div class="row no-gutters" v-if="users" key="competition-users">
        <!-- Header -->
        <div class="col-12 col-lg-8 leaderboard">
          <div class="row">
            <div class="col-12">
              <h1 class="page-title">{{ $t('pages.title.leaderboard') }}</h1>
            </div>
          </div>
          <!-- Leader card -->
          <div v-if="!isEmptyCompetition" class="row places-row no-gutters">
            <div class="col-12">
              <leader-user-card
                :user="users[0]"
                class="place-first"
                :place="1"
              />
              <user-card
                :user="users[0]"
                :place="1"
                class="place-first-simple"
              />
            </div>
          </div>
          <!-- Other users -->
          <div class="row no-gutters others">
            <div
              class="col-12 col-md-6 col-xl-4"
              v-for="(user, index) in otherUsers"
              :key="index"
            >
              <user-card :user="user" :place="getPlaceForIndex(index)" />
            </div>
            <no-user-card v-if="users.length < 2" />
            <no-user-card v-if="users.length < 3" />
            <div class="col-12" v-if="users.length < 2">
              <div class="empty-team">
                <lottie-player
                  src="/static/images/animations/team.json"
                  background="transparent"
                  speed="0.7"
                  style="width: 300px; height: 300px"
                  loop
                  autoplay
                  class="empty-team-image"
                ></lottie-player>
                <h2>{{ $t('competition.team.empty.title') }}</h2>
                <base-button
                  @click="invite"
                  :text="$t('competition.team.invite.buttonText')"
                  v-if="loggedInUser.role == 1"
                  class="invite-button"
                />
              </div>
            </div>
          </div>
        </div>
        <!-- Recent feedback list -->
        <div class="col-12 col-lg-4">
          <div class="row no-gutters p-2">
            <company-progress
              v-if="shouldShowProgress"
              :progress="progress"
              @showInviteModal="invite"
              @showFeedbackModal="showFeedbackModal"
            />
          </div>
          <div class="row no-gutters p-2">
            <recent-feedback-list
              :transactions="transactions"
              :recommendation="recommendation"
            />
          </div>
        </div>
      </div>
      <competition-shimming
        class="row no-gutters content-loader"
        key="competition-content-loader"
        v-show="!users"
      />
    </transition-group>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import LeaderUserCard from '@/components/shared/users/LeaderUserCard';
import UserCard from '@/components/shared/users/UserCard';
import NoUserCard from '@/components/shared/users/NoUserCard';
import CompetitionShimming from '@/components/competition/CompetitionShimming';
import RecentFeedbackList from '@/components/competition/RecentFeedbackList';
import CompanyProgress from '@/components/competition/CompanyProgress';
import BaseButton from '@/components/shared/elements/BaseButton';
import InviteModal from '@/components/shared/modals/InviteModal';
import FeedbackModal from '@/components/shared/modals/FeedbackModal';

export default {
  name: 'Competition',
  components: {
    LeaderUserCard,
    UserCard,
    NoUserCard,
    CompetitionShimming,
    BaseButton,
    RecentFeedbackList,
    CompanyProgress,
  },
  props: {},
  data() {
    return {
      recommendation: undefined,
      progress: undefined,
    };
  },
  created() {
    this.fetchData();
    setInterval(() => {
      this.fetchData();
    }, 60000);
  },
  mounted() {
    if (this.$route.query.status === 'new_registration') {
      window.userGuiding.previewGuide(52727);
    }
  },
  methods: {
    ...mapActions({
      getCurrentLeaderboard: 'leaderboard/getCurrentLeaderboard',
      getTransactions: 'leaderboard/getTransactions',
      fetchLoggedInProfile: 'users/fetchLoggedInProfile',
      getOpenExchanges: 'exchanges/getOpenExchanges',
      getRecommendation: 'transactions/recommendation',
      companyProgress: 'companies/companyProgress',
    }),
    async fetchData() {
      this.fetchLoggedInProfile();
      this.getCurrentLeaderboard();
      this.getTransactions();
      this.getOpenExchanges();
      this.getRecommendation().then(recommendationList => {
        this.recommendation = recommendationList[Math.floor(Math.random()*recommendationList.length)]
      });
      this.progress = await this.companyProgress({
        id: this.loggedInUser.companyId,
      });
    },
    invite() {
      this.$eventBus.showModal({
        bind: {
          is: InviteModal,
        },
      });
      this.$gtag.event('showInvitation', {});
    },
    getPlaceForIndex(index) {
      if (this.isEmptyCompetition) {
        return index + 1;
      }
      return index + 2;
    },
    showFeedbackModal() {
      this.$eventBus.showModal({
        bind: {
          is: FeedbackModal,
        },
      });
    },
  },
  computed: {
    ...mapGetters({
      users: 'leaderboard/currentLeaderboard',
      transactions: 'leaderboard/transactions',
      loggedInUser: 'users/loggedInUserProfile',
    }),
    isEmptyCompetition() {
      return this.users[0] && this.users[0].votes < 1;
    },
    otherUsers() {
      let otherUsers = [];
      if (!this.users) {
        return otherUsers;
      }
      if (this.isEmptyCompetition) {
        otherUsers = this.users.slice();
      } else {
        otherUsers = this.users.slice(1);
      }
      if (
        this.loggedInUser.company.privacyLevel > 0 &&
        this.loggedInUser.role !== 1
      ) {
        const secondAndThird = otherUsers.slice(0, 2);
        const theRest = otherUsers
          .splice(2)
          .sort((a, b) => a.userName.localeCompare(b.userName));
        otherUsers = [...secondAndThird, ...theRest];
      }
      return otherUsers;
    },
    shouldShowProgress() {
      if (this.progress) {
        const hasOpen =
          Object.values(this.progress).filter((x) => x).length < 4;
        return hasOpen && this.loggedInUser.role == 1;
      }
      return false;
    },
  },
};
</script>

<style lang="scss" scoped>
#competition {
  .leaderboard {
    padding: 0 3%;
  }

  .user-card {
    margin: 20px;
  }
  .place-second,
  .place-third {
    padding-top: 20%;
  }

  .empty-team,
  .need-config {
    margin: 40px auto;
    .config-image {
      max-height: 250px;
    }
    .empty-team-image {
      max-height: 150px;
      margin: 0 auto;
    }
    .invite-button,
    .config-button {
      margin: 0 auto;
      width: 50%;
      >>> .base-button {
        font-size: 80%;
      }
    }
    .config-button {
      margin-top: 10px;
      >>> .base-button {
        background-color: #50e3c2;
        &:hover {
          background-color: #1d9e81;
        }
      }
    }
  }
  .others {
    >>> .user-card {
      margin: 20px;
    }
  }
  .v-leave-active {
    display: none;
  }

  .place-first-simple {
    display: flex;
  }
  .place-first {
    display: none;
  }
  @media (min-width: 992px) {
    .place-first-simple {
      display: none;
    }
    .place-first {
      display: flex;
    }
  }
  .guide-link {
    padding-top: 10px;
    cursor: pointer;
    &:hover {
      color: #622d90;
    }
  }
}
</style>
